/**
 * InspirationListPage.jsx - Lista articoli con filtri
 * ====================================================
 * 100% CMS-driven - NO HARDCODED TEXT
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  Search, Clock, Calendar, Bookmark, Filter, X, ChevronLeft, ChevronRight,
  LayoutGrid, List, Loader2, SlidersHorizontal, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import supabase from '../lib/supabase';
import Header from '../components/Header';
import Footer from '../components/Footer';

const POSTS_PER_PAGE = 10;

const InspirationListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [config, setConfig] = useState(null);
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [viewMode, setViewMode] = useState('grid');
  
  // Filters from URL
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const searchQuery = searchParams.get('q') || '';
  const selectedCategory = searchParams.get('category') || '';
  const selectedMaterial = searchParams.get('material') || '';
  const selectedTags = searchParams.get('tags')?.split(',').filter(Boolean) || [];
  const sortBy = searchParams.get('sort') || 'recent';

  // Load config and filter options
  useEffect(() => {
    const loadFilters = async () => {
      try {
        // Config
        const { data: configData } = await supabase
          .from('blueprint_inspiration_config')
          .select('*')
          .eq('lang', 'it')
          .single();
        if (configData) setConfig(configData);

        // Categories
        const { data: catData } = await supabase
          .from('blueprint_post_categories')
          .select('*')
          .eq('is_active', true)
          .order('order_index');
        if (catData) setCategories(catData);

        // Popular tags
        const { data: tagData } = await supabase
          .from('blueprint_post_tags')
          .select('*')
          .eq('is_popular', true)
          .order('name');
        if (tagData) setTags(tagData);

        // Material types (from materials table categories)
        setMaterialTypes([
          { slug: 'marmo', name: 'Marmo' },
          { slug: 'quarzite', name: 'Quarzite' },
          { slug: 'onice', name: 'Onice' },
          { slug: 'granito', name: 'Granito' },
          { slug: 'travertino', name: 'Travertino' },
          { slug: 'arenaria', name: 'Arenaria' },
          { slug: 'altre-pietre', name: 'Altre pietre' }
        ]);
      } catch (err) {
        console.error('Error loading filters:', err);
      }
    };
    loadFilters();
  }, []);

  // Load posts with filters
  useEffect(() => {
    const loadPosts = async () => {
      setIsLoading(true);
      try {
        let query = supabase
          .from('blueprint_posts')
          .select(`
            *,
            category:blueprint_post_categories(slug, name_it, color),
            author:blueprint_authors(name, avatar_url)
          `, { count: 'exact' })
          .eq('status', 'published');

        // Apply filters
        if (selectedCategory) {
          const { data: cat } = await supabase
            .from('blueprint_post_categories')
            .select('id')
            .eq('slug', selectedCategory)
            .single();
          if (cat) query = query.eq('category_id', cat.id);
        }

        if (searchQuery) {
          query = query.or(`title_it.ilike.%${searchQuery}%,excerpt_it.ilike.%${searchQuery}%`);
        }

        // Sort
        if (sortBy === 'popular') {
          query = query.order('view_count', { ascending: false });
        } else if (sortBy === 'reading_time') {
          query = query.order('reading_time', { ascending: true });
        } else {
          query = query.order('published_at', { ascending: false });
        }

        // Pagination
        const from = (currentPage - 1) * POSTS_PER_PAGE;
        query = query.range(from, from + POSTS_PER_PAGE - 1);

        const { data, count, error } = await query;
        
        if (error) throw error;
        setPosts(data || []);
        setTotalCount(count || 0);
      } catch (err) {
        console.error('Error loading posts:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadPosts();
  }, [currentPage, searchQuery, selectedCategory, sortBy]);

  // Update URL params
  const updateFilters = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    if (updates.category !== undefined || updates.q !== undefined) {
      newParams.delete('page');
    }
    setSearchParams(newParams);
  };

  const resetFilters = () => {
    setSearchParams({});
  };

  // Text helper
  const t = (key, fallback) => config?.[key] || fallback;

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Pagination
  const totalPages = Math.ceil(totalCount / POSTS_PER_PAGE);

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      <Header />
      
      <main className="flex-1 pt-20">
        <div className="max-w-[1800px] mx-auto px-6 md:px-12 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <p className="text-[#B08B5C] text-sm font-semibold tracking-wider mb-2">
              {t('hero_label', 'ISPIRAZIONE')}
            </p>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <h1 className="text-3xl md:text-4xl font-serif text-white mb-2">
                  {t('list_title', 'Tutti gli articoli')}
                </h1>
                <p className="text-white/50">
                  {t('hero_description', 'Approfondimenti, guide e progetti dal mondo dell\'architettura e del design.')}
                </p>
              </div>
              
              {/* Search */}
              <div className="relative w-full md:w-80">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => updateFilters({ q: e.target.value })}
                  placeholder={t('search_placeholder', 'Cerca articoli, materiali, guide...')}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-[#121212] border border-[#1a1a1a] text-white placeholder-white/30 focus:border-[#B08B5C] focus:outline-none text-sm"
                />
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex gap-8">
            {/* Sidebar Filters */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-24 space-y-6">
                {/* Filter Header */}
                <div className="flex items-center justify-between">
                  <h3 className="text-white font-medium">{t('filter_title', 'Filtri')}</h3>
                  <button 
                    onClick={resetFilters}
                    className="text-[#B08B5C] text-sm hover:underline"
                  >
                    {t('filter_reset', 'Resetta')}
                  </button>
                </div>

                {/* Search Filter */}
                <div>
                  <label className="text-white/50 text-xs uppercase tracking-wider mb-2 block">
                    {t('filter_search_label', 'Cerca per parola chiave')}
                  </label>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => updateFilters({ q: e.target.value })}
                    placeholder={t('filter_search_placeholder', 'Es. marmo, onice, cucina...')}
                    className="w-full px-3 py-2 rounded-lg bg-[#121212] border border-[#1a1a1a] text-white text-sm placeholder-white/30 focus:border-[#B08B5C] focus:outline-none"
                  />
                </div>

                {/* Categories */}
                <div>
                  <label className="text-white/50 text-xs uppercase tracking-wider mb-3 block">
                    {t('filter_categories_label', 'Categorie')}
                  </label>
                  <div className="space-y-2">
                    <label 
                      className="flex items-center gap-2 cursor-pointer group"
                      onClick={() => updateFilters({ category: '' })}
                    >
                      <div className={`w-4 h-4 rounded border ${!selectedCategory ? 'bg-[#B08B5C] border-[#B08B5C]' : 'border-white/20 group-hover:border-white/40'} flex items-center justify-center transition-colors`}>
                        {!selectedCategory && <Check className="w-3 h-3 text-black" />}
                      </div>
                      <span className={`text-sm ${!selectedCategory ? 'text-white' : 'text-white/60'} group-hover:text-white transition-colors`}>
                        {t('filter_all_categories', 'Tutte le categorie')}
                      </span>
                    </label>
                    {categories.map((cat) => (
                      <label 
                        key={cat.id} 
                        className="flex items-center gap-2 cursor-pointer group"
                        onClick={() => updateFilters({ category: selectedCategory === cat.slug ? '' : cat.slug })}
                      >
                        <div className={`w-4 h-4 rounded border ${selectedCategory === cat.slug ? 'bg-[#B08B5C] border-[#B08B5C]' : 'border-white/20 group-hover:border-white/40'} flex items-center justify-center transition-colors`}>
                          {selectedCategory === cat.slug && <Check className="w-3 h-3 text-black" />}
                        </div>
                        <span className={`text-sm ${selectedCategory === cat.slug ? 'text-white' : 'text-white/60'} group-hover:text-white transition-colors`}>
                          {cat.name_it || cat.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Materials */}
                <div>
                  <label className="text-white/50 text-xs uppercase tracking-wider mb-3 block">
                    {t('filter_materials_label', 'Materiali')}
                  </label>
                  <div className="space-y-2">
                    <label 
                      className="flex items-center gap-2 cursor-pointer group"
                      onClick={() => updateFilters({ material: '' })}
                    >
                      <div className={`w-4 h-4 rounded border ${!selectedMaterial ? 'bg-[#B08B5C] border-[#B08B5C]' : 'border-white/20 group-hover:border-white/40'} flex items-center justify-center transition-colors`}>
                        {!selectedMaterial && <Check className="w-3 h-3 text-black" />}
                      </div>
                      <span className={`text-sm ${!selectedMaterial ? 'text-white' : 'text-white/60'} group-hover:text-white transition-colors`}>
                        {t('filter_all_materials', 'Tutti i materiali')}
                      </span>
                    </label>
                    {materialTypes.map((mat) => (
                      <label 
                        key={mat.slug} 
                        className="flex items-center gap-2 cursor-pointer group"
                        onClick={() => updateFilters({ material: selectedMaterial === mat.slug ? '' : mat.slug })}
                      >
                        <div className={`w-4 h-4 rounded border ${selectedMaterial === mat.slug ? 'bg-[#B08B5C] border-[#B08B5C]' : 'border-white/20 group-hover:border-white/40'} flex items-center justify-center transition-colors`}>
                          {selectedMaterial === mat.slug && <Check className="w-3 h-3 text-black" />}
                        </div>
                        <span className={`text-sm ${selectedMaterial === mat.slug ? 'text-white' : 'text-white/60'} group-hover:text-white transition-colors`}>
                          {mat.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Popular Tags */}
                <div>
                  <label className="text-white/50 text-xs uppercase tracking-wider mb-3 block">
                    {t('filter_tags_label', 'Tag popolari')}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <button
                        key={tag.id}
                        onClick={() => {
                          const newTags = selectedTags.includes(tag.slug)
                            ? selectedTags.filter(t => t !== tag.slug)
                            : [...selectedTags, tag.slug];
                          updateFilters({ tags: newTags.join(',') || '' });
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${
                          selectedTags.includes(tag.slug)
                            ? 'bg-[#B08B5C] text-black'
                            : 'bg-[#1a1a1a] text-white/60 hover:text-white hover:bg-[#252525]'
                        }`}
                      >
                        {tag.name_it || tag.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Apply Button - not needed since filters apply automatically */}
              </div>
            </aside>

            {/* Posts Grid */}
            <div className="flex-1">
              {/* Results Header */}
              <div className="flex items-center justify-between mb-6">
                <p className="text-white/50 text-sm">
                  {t('results_text', '{count} articoli trovati').replace('{count}', totalCount)}
                </p>
                
                <div className="flex items-center gap-4">
                  {/* Sort */}
                  <div className="flex items-center gap-2">
                    <span className="text-white/40 text-sm">{t('sort_label', 'Ordina per:')}</span>
                    <select
                      value={sortBy}
                      onChange={(e) => updateFilters({ sort: e.target.value })}
                      className="bg-[#121212] border border-[#1a1a1a] text-white text-sm rounded-lg px-3 py-1.5 focus:border-[#B08B5C] focus:outline-none"
                    >
                      <option value="recent">{t('sort_recent', 'Più recenti')}</option>
                      <option value="popular">{t('sort_popular', 'Più popolari')}</option>
                      <option value="reading_time">{t('sort_reading_time', 'Tempo di lettura')}</option>
                    </select>
                  </div>
                  
                  {/* View Toggle */}
                  <div className="flex bg-[#121212] rounded-lg border border-[#1a1a1a] p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-white/10 text-white' : 'text-white/40'}`}
                    >
                      <LayoutGrid className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-white/10 text-white' : 'text-white/40'}`}
                    >
                      <List className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Loading */}
              {isLoading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-[#B08B5C]" />
                </div>
              ) : posts.length === 0 ? (
                <div className="text-center py-20">
                  <p className="text-white/50">Nessun articolo trovato</p>
                </div>
              ) : (
                <>
                  {/* Grid View */}
                  <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-5' : 'grid-cols-1'}`}>
                    {posts.map((post) => (
                      <Link
                        key={post.id}
                        to={`/inspiration/${post.slug}`}
                        className={`group ${viewMode === 'list' ? 'flex gap-6' : ''}`}
                      >
                        {/* Image */}
                        <div className={`${viewMode === 'list' ? 'w-48 flex-shrink-0' : ''} aspect-[4/5] rounded-xl overflow-hidden bg-[#121212] relative`}>
                          {post.cover_image_url && (
                            <img 
                              src={post.cover_image_url} 
                              alt={post.title_it || post.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          )}
                          {post.category && (
                            <span 
                              className="absolute top-3 left-3 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-full"
                              style={{ backgroundColor: post.category.color || '#B08B5C', color: '#0a0a0a' }}
                            >
                              {post.category.name_it?.split(' ')[0]}
                            </span>
                          )}
                          <button className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center text-white/60 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity">
                            <Bookmark className="w-4 h-4" />
                          </button>
                        </div>
                        
                        {/* Content */}
                        <div className={viewMode === 'list' ? 'flex-1 py-2' : 'mt-4'}>
                          <h3 className="text-white font-medium mb-2 line-clamp-2 group-hover:text-[#B08B5C] transition-colors">
                            {post.title_it || post.title}
                          </h3>
                          <p className="text-white/40 text-sm mb-3 line-clamp-2">
                            {post.excerpt_it || post.excerpt}
                          </p>
                          <div className="flex items-center gap-3 text-white/30 text-xs">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {post.reading_time} min
                            </span>
                            <span>•</span>
                            <span>{formatDate(post.published_at)}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-12">
                      <button
                        onClick={() => updateFilters({ page: String(currentPage - 1) })}
                        disabled={currentPage === 1}
                        className="w-10 h-10 rounded-lg bg-[#121212] border border-[#1a1a1a] flex items-center justify-center text-white/60 hover:text-white disabled:opacity-30"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      
                      {[...Array(Math.min(5, totalPages))].map((_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => updateFilters({ page: String(pageNum) })}
                            className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm ${
                              currentPage === pageNum
                                ? 'bg-[#B08B5C] text-black font-medium'
                                : 'bg-[#121212] border border-[#1a1a1a] text-white/60 hover:text-white'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      {totalPages > 5 && currentPage < totalPages - 2 && (
                        <>
                          <span className="text-white/30">...</span>
                          <button
                            onClick={() => updateFilters({ page: String(totalPages) })}
                            className="w-10 h-10 rounded-lg bg-[#121212] border border-[#1a1a1a] flex items-center justify-center text-white/60 hover:text-white text-sm"
                          >
                            {totalPages}
                          </button>
                        </>
                      )}
                      
                      <button
                        onClick={() => updateFilters({ page: String(currentPage + 1) })}
                        disabled={currentPage === totalPages}
                        className="w-10 h-10 rounded-lg bg-[#121212] border border-[#1a1a1a] flex items-center justify-center text-white/60 hover:text-white disabled:opacity-30"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default InspirationListPage;
